import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../lib/usePageTitle';
import { formatDateShort } from '../lib/dates';
import { AppLayout } from '../components/AppLayout';
import { api } from '../lib/api';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  leagueId: string | null;
  data: { leagueMatchdayId?: string };
  readAt: string | null;
  createdAt: string;
}

export function NotificationsPage() {
  usePageTitle('Notifiche');
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<{ unread: number; notifications: NotificationItem[] }>('/notifications'),
  });

  // Aprire la pagina segna tutto come letto (dopo il primo render).
  useEffect(() => {
    if (query.data && query.data.unread > 0) {
      api('/notifications/read', { method: 'POST', body: {} })
        .then(() => queryClient.invalidateQueries({ queryKey: ['notifications'] }))
        .catch(() => {});
    }
  }, [query.data, queryClient]);

  return (
    <AppLayout title="Notifiche">
      {query.isLoading && (
        <p role="status" className="mt-6">Caricamento notifiche…</p>
      )}

      {query.data && query.data.notifications.length === 0 && (
        <p className="mt-6 text-lg">Nessuna notifica per ora. 🐼</p>
      )}

      {query.data && query.data.notifications.length > 0 && (
        <ul className="mt-6 flex flex-col divide-y divide-ink-200 rounded-2xl border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
          {query.data.notifications.map((n) => (
            <li key={n.id} className="flex flex-col gap-1 px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-semibold">
                  {!n.readAt && (
                    <>
                      <span
                        aria-hidden="true"
                        className="mr-2 inline-block h-2.5 w-2.5 rounded-full bg-panda-600"
                      />
                      <span className="visually-hidden">Non letta: </span>
                    </>
                  )}
                  {n.title}
                </h2>
                <span className="text-sm text-ink-600 dark:text-ink-300">
                  {formatDateShort(n.createdAt)}
                </span>
              </div>
              <p className="text-ink-700 dark:text-ink-200">{n.body}</p>
              {n.leagueId && n.data.leagueMatchdayId && (
                <p>
                  <Link
                    to={`/leghe/${n.leagueId}/giornate/${n.data.leagueMatchdayId}`}
                    className="font-medium text-panda-700 underline dark:text-panda-300"
                  >
                    Vai alla giornata
                  </Link>
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </AppLayout>
  );
}
